import React, { ReactNode } from 'react';
import { Text } from '@simplybusiness/mobius';
import './Page.css';
import { useLocation } from 'react-router';
import { useSelector } from 'react-redux';
import NavContent from '../NavContent';
import { getUser, isUserLoggedIn } from '../../resources/user/selectors';

const publicRoutes = [
  '/sign-in',
  '/sign-in/password',
  '/sign-in/token',
  '/reset-forgotten-password',
  '/forgot-password',
  '/create-password',
  '/confirm-email',
];

interface Props {
  children: ReactNode,
  dataTestId?: string,
  title?: string,
  className?: string,
  backgroundColor?: string,
  borderRadius?: string,
}

const PageSection = ({
  children,
  dataTestId = 'page-section',
  title,
}: Props): JSX.Element => (
  <div data-testid={dataTestId} title={title}>
    <div className="content-wrapper">
      {title && <Text variant="h1" elementType="h1" className="page-title">{title}</Text>}
      {children}
    </div>
  </div>
);

const filterChildrenForType = (children: ReactNode, type: React.ElementType) => (
  React.Children.toArray(children).filter(child => React.isValidElement(child) && child.type === type)
);

const Page = ({ children, className, title }: Props): JSX.Element => {
  const location = useLocation();
  const user = useSelector(getUser);
  const isNavExcludedRoute = location.pathname.startsWith('/cancel-request/');
  const shouldRenderNav = ! publicRoutes.includes(location.pathname) && ! isNavExcludedRoute && isUserLoggedIn(user);

  const hasSections = filterChildrenForType(children, PageSection).length > 0;

  return (
    <div className="page-wrapper">
      {shouldRenderNav
        && (
        <nav className="nav-wrapper" aria-label="Primary navigation">
          <NavContent />
        </nav>
        )
      }
      <div className={`page ${className || ''}`} title={title}>
        {hasSections ? children : <PageSection title={title}>{children}</PageSection>}
      </div>
    </div>
  );
};

Page.Section = PageSection;

export default Page;
